import { buildI18n } from "@repo/shared";
import type { ClientCostCategory, LocalizedString } from "@repo/shared/types";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TranslationButton } from "@/components/ui/translation-button";
import * as clientCostCategoriesApi from "@/lib/api/client-cost-categories";
import {
  useClientCostCategories,
  useCreateClientCostCategory,
  useDeleteClientCostCategory,
  useToggleClientCostCategoryActive,
  useUpdateClientCostCategory,
} from "@/lib/hooks/use-client-cost-categories";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import { useUserLanguage } from "@/lib/i18n";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./client-cost-categories-wrapper.i18n";

export function ClientCostCategoriesWrapper({
  supportedLanguages,
}: {
  supportedLanguages: string[];
}) {
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  const { clientCostCategories } = useClientCostCategories();
  const createCategory = useCreateClientCostCategory();
  const updateCategory = useUpdateClientCostCategory();
  const deleteCategory = useDeleteClientCostCategory();
  const toggleActive = useToggleClientCostCategoryActive();

  const isPending =
    createCategory.isPending ||
    updateCategory.isPending ||
    deleteCategory.isPending ||
    toggleActive.isPending;

  const getName = (i18n: LocalizedString) => localize(i18n);

  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState<ClientCostCategory | null>(null);
  const [editName, setEditName] = useState("");

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  const handleAdd = () => {
    if (!newName.trim()) return;
    createCategory.mutate(
      { nameI18n: buildI18n(newName, userLanguage, supportedLanguages) },
      {
        onSuccess: () => {
          setNewName("");
          setIsAdding(false);
        },
        onError: (err: Error) => showAlert(t.error, err.message || t.failedToAdd, "destructive"),
      },
    );
  };

  const handleUpdate = () => {
    if (!editing || !editName.trim()) return;
    const updatedI18n: LocalizedString = { ...editing.nameI18n, [userLanguage]: editName };
    updateCategory.mutate(
      { id: editing.id, data: { nameI18n: updatedI18n } },
      {
        onSuccess: () => {
          setEditing(null);
          setEditName("");
        },
        onError: (err: Error) => showAlert(t.error, err.message || t.failedToUpdate, "destructive"),
      },
    );
  };

  const handleTranslationSave = (id: string, nameI18n: LocalizedString) => {
    updateCategory.mutate(
      { id, data: { nameI18n } },
      {
        onError: (err: Error) => showAlert(t.error, err.message || t.failedToUpdate, "destructive"),
      },
    );
  };

  // QL-168 — a category still assigned to clients shows why straight away, before the confirmation.
  const { requestDelete, checkingId } = useDeleteCheck({
    check: clientCostCategoriesApi.checkDelete,
    onAllowed: (id) => {
      setToDelete(id);
      setConfirmDeleteOpen(true);
    },
    onBlocked: (reason) => showAlert(t.error, reason || t.failedToDelete, "destructive"),
  });

  const handleDelete = () => {
    if (!toDelete) return;
    deleteCategory.mutate(toDelete, {
      onSuccess: () => setToDelete(null),
      onError: (err: Error) => {
        showAlert(t.error, err.message || t.failedToDelete, "destructive");
        setToDelete(null);
      },
    });
  };

  const handleToggleActive = (id: string) => {
    toggleActive.mutate(id, {
      onError: (err: Error) => showAlert(t.error, err.message || t.failedToUpdate, "destructive"),
    });
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm text-muted-foreground">{t.description}</p>
          {!isAdding && (
            <Button onClick={() => setIsAdding(true)} disabled={isPending}>
              {t.addCategory}
            </Button>
          )}
        </div>

        {clientCostCategories.length === 0 && !isAdding ? (
          <EmptyState>{t.noCategories}</EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.active}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clientCostCategories.map((cat) => (
                <TableRow key={cat.id}>
                  <TableCell>
                    {editing?.id === cat.id ? (
                      <div className="flex gap-2">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          placeholder={t.categoryName}
                          autoFocus
                          disabled={isPending}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleUpdate();
                            if (e.key === "Escape") {
                              setEditing(null);
                              setEditName("");
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          onClick={handleUpdate}
                          disabled={isPending}
                          loading={updateCategory.isPending}
                        >
                          {t.save}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditing(null);
                            setEditName("");
                          }}
                          disabled={isPending}
                        >
                          {t.cancel}
                        </Button>
                      </div>
                    ) : (
                      <span className="font-medium">{getName(cat.nameI18n)}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      aria-label={`${t.active}: ${getName(cat.nameI18n)}`}
                      checked={cat.isActive}
                      onCheckedChange={() => handleToggleActive(cat.id)}
                      disabled={isPending}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    {editing?.id !== cat.id && (
                      <div className="flex items-center justify-end">
                        <Button
                          size="icon"
                          tooltip={t.edit}
                          variant="ghost"
                          onClick={() => {
                            setEditing(cat);
                            setEditName(getName(cat.nameI18n));
                          }}
                          disabled={isPending}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <TranslationButton
                          value={cat.nameI18n}
                          onSave={(nameI18n) => handleTranslationSave(cat.id, nameI18n)}
                          supportedLanguages={supportedLanguages}
                          disabled={isPending}
                        />
                        <Button
                          size="icon"
                          tooltip={t.delete}
                          variant="ghost"
                          loading={checkingId === cat.id}
                          onClick={() => requestDelete(cat.id)}
                          disabled={isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {isAdding && (
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t.placeholder}
              autoFocus
              disabled={isPending}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
                if (e.key === "Escape") {
                  setIsAdding(false);
                  setNewName("");
                }
              }}
            />
            <Button size="sm" onClick={handleAdd} disabled={isPending}>
              {t.add}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setIsAdding(false);
                setNewName("");
              }}
              disabled={isPending}
            >
              {t.cancel}
            </Button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteCategory}
        description={t.deleteDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
